import { useState, useEffect, useRef } from "react";
import Dialog from "@mui/material/Dialog";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
    Schedule as ScheduleIcon,
    Assignment as AssignmentIcon,
    Monitor as MonitorIcon,
    Assessment as AssessmentIcon,
    ViewTimeline as TemplatesIcon,
    Add as AddIcon,
    Edit as EditIcon,
    DeleteOutline as DeleteIcon,
    Visibility as VisibilityIcon,
    PlayArrow as PlayIcon,
    Stop as StopIcon,
    People as PeopleIcon,
    LocationOn as LocationIcon,
    Search as SearchIcon,
    FilterList as FilterIcon,
    CheckCircle as CheckCircleIcon,
    ViewModule as GridViewIcon,
    ViewList as ListViewIcon,
    CalendarToday as CalendarIcon,
    AccessTime as AccessTimeIcon,
    TrendingUp as TrendingUpIcon,
    Info as InfoIcon,
    Book as BookIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Close as CloseIcon,
} from "@mui/icons-material";
import { DocsIcon } from "./svg";
import { Badge } from "../pages/ShiftManagement/components/ui/Badge";

const Shortcut = ({ children }) => (
    <Badge variant="secondary" className="font-mono font-semibold text-xs">
        {children}
    </Badge>
);

export default function ShiftManagementDocs({ showButton = true, openD: externalOpenD, onClose: externalOnClose }) {
    const [internalOpenD, setInternalOpenD] = useState(false);
    const [lang, setLang] = useState("en");
    const [expandedSections, setExpandedSections] = useState(new Set([0]));
    const [searchQuery, setSearchQuery] = useState("");
    const [activeSection, setActiveSection] = useState(null);
    const [scrollProgress, setScrollProgress] = useState(0);
    const scrollContainerRef = useRef(null);
    const sectionRefs = useRef({});
    
    const openD = externalOpenD !== undefined ? externalOpenD : internalOpenD;
    const handleClose = () => {
        if (externalOnClose) {
            externalOnClose();
        } else {
            setInternalOpenD(false);
        }
    };

    const toggleLang = () => {
        setLang((prev) => (prev === "en" ? "ar" : "en"));
    };

    const toggleSection = (index) => {
        setExpandedSections((prev) => {
            const newSet = new Set();
            // If clicking on an already expanded section, close it
            // Otherwise, close all and open only the clicked one
            if (!prev.has(index)) {
                newSet.add(index);
            }
            return newSet;
        });
    };

    const scrollToSection = (index) => {
        const element = sectionRefs.current[index];
        if (element && scrollContainerRef.current) {
            const offset = 120;
            const elementPosition = element.offsetTop;
            const offsetPosition = elementPosition - offset;

            scrollContainerRef.current.scrollTo({
                top: offsetPosition,
                behavior: "smooth"
            });
            setActiveSection(index);
            setExpandedSections((prev) => new Set([...prev, index]));
        }
    };

    useEffect(() => {
        const handleScroll = () => {
            if (!scrollContainerRef.current) return;
            
            const container = scrollContainerRef.current;
            const scrollTop = container.scrollTop;
            const scrollHeight = container.scrollHeight - container.clientHeight;
            const progress = (scrollTop / scrollHeight) * 100;
            setScrollProgress(progress);
            
            // Update active section based on scroll position
            const scrollPosition = scrollTop + 200;
            
            Object.keys(sectionRefs.current).forEach((key) => {
                const index = parseInt(key);
                const element = sectionRefs.current[index];
                if (element) {
                    const { offsetTop, offsetHeight } = element;
                    if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
                        setActiveSection(index);
                    }
                }
            });
        };

        const container = scrollContainerRef.current;
        if (container) {
            container.addEventListener('scroll', handleScroll);
            return () => container.removeEventListener('scroll', handleScroll);
        }
    }, []);

    const docContent = {
        en: {
            dir: "ltr",
            title: "Shift Management Documentation",
            subtitle: "Complete guide to scheduling, assigning, and monitoring driver shifts",
            buttonText: "العربية",
            searchPlaceholder: "Search documentation...",
            tableHeaders: {
                icon: "Icon",
                action: "Action",
                description: "Description"
            },
            sections: [
                {
                    title: "Overview",
                    content:
                        "The Shift Management module is a comprehensive system within the Barq Admin Portal designed for scheduling, assigning, and monitoring driver shifts. It provides a centralized platform to manage shift schedules, assign drivers to shifts, monitor real-time driver activity, track compliance, and generate reusable shift templates. The interface is organized into five main sections: Shift Session, Shift Scheduler, Driver Assignment, Live Monitor, and Compliance Reports, each offering specialized tools for efficient shift management.",
                    color: "blue",
                    icon: BookIcon,
                },
                {
                    title: "Shift Session",
                    content:
                        "Shift Session is the primary interface for managing individual shift schedules. It allows you to create, view, edit, activate, and cancel shifts. Each shift represents a scheduled work period with specific time slots, location requirements, and driver capacity needs. The interface supports both card and table views for flexible data visualization.",
                    color: "blue",
                    icon: ScheduleIcon,
                    tags: [
                        "Shift cards display key information including start/end times, required drivers count, assigned drivers, available slots, status (Draft/Active/Completed/Cancelled), and location details.",
                        "Draft shifts can be activated to make them available for driver assignment. Active shifts can be cancelled if needed.",
                        "Statistics cards show total shifts, active shifts, draft shifts, and completed shifts at a glance for quick overview.",
                        "Filters allow you to narrow down shifts by city, neighborhood, status, date range, and search terms for efficient management."
                    ],
                    subsections: [
                        { title: "View Modes", description: "Toggle between card view (visual cards) and table view (detailed spreadsheet format) using the view mode buttons.", icon: GridViewIcon },
                        { title: "Filters", description: "Filter shifts by city, neighborhood, status (Draft/Active/Completed/Cancelled), date range, and search keywords.", icon: FilterIcon },
                        { title: "Pagination", description: "Navigate through multiple pages of shifts with 12 items per page. Page state is saved in browser storage.", icon: ScheduleIcon },
                        { title: "Actions", description: "Click on shift cards to view details, edit shifts, activate draft shifts, or cancel active shifts.", icon: EditIcon },
                    ],
                    actions: [
                        {
                            icon: <VisibilityIcon />,
                            title: "View Shift Details",
                            description: "Opens a detailed view of the selected shift showing all assignments, driver statistics, and compliance information.",
                            shortcut: null,
                        },
                        {
                            icon: <EditIcon />,
                            title: "Edit Shift",
                            description: "Allows modification of shift details such as time, required drivers, and location settings before activation.",
                            shortcut: null,
                        },
                        {
                            icon: <PlayIcon />,
                            title: "Activate Shift",
                            description: "Converts a draft shift to active status, making it available for driver assignment and operational use.",
                            shortcut: null,
                        },
                        {
                            icon: <StopIcon />,
                            title: "Cancel Shift",
                            description: "Cancels an active shift and all pending/confirmed assignments. Cannot cancel shifts with checked-in or completed drivers.",
                            shortcut: null,
                        },
                        {
                            icon: <GridViewIcon />,
                            title: "Card View",
                            description: "Displays shifts as visual cards with key information visible at a glance. Better for quick scanning and overview.",
                            shortcut: null,
                        },
                        {
                            icon: <ListViewIcon />,
                            title: "Table View",
                            description: "Displays shifts in a detailed table format with sortable columns. Better for detailed analysis and comparison.",
                            shortcut: null,
                        },
                    ],
                },
                {
                    title: "Shift Scheduler",
                    content:
                        "Shift Scheduler enables you to create reusable shift patterns that can automatically generate multiple shifts. Templates support various recurrence types including daily, weekly, specific dates, and custom date ranges. This feature is ideal for managing recurring schedules and reducing manual shift creation effort.",
                    color: "purple",
                    icon: TemplatesIcon,
                    tags: [
                        "Templates can be set to active or inactive status. Only active templates can generate shifts automatically.",
                        "Weekly templates allow you to specify which days of the week the shift should occur (e.g., Monday, Wednesday, Friday).",
                        "Templates can have valid from/until dates to control when shifts should be generated, and exclude specific dates.",
                        "You can assign specific drivers to templates, and they will be automatically assigned when shifts are generated from the template."
                    ],
                    subsections: [
                        { title: "Recurrence Types", description: "Choose from daily, weekly, specific date, or custom range recurrence patterns for flexible scheduling.", icon: CalendarIcon },
                        { title: "Template Status", description: "Activate or deactivate templates. Inactive templates won't generate new shifts but existing shifts remain.", icon: CheckCircleIcon },
                        { title: "Driver Assignment", description: "Pre-assign drivers to templates so they are automatically assigned when shifts are generated.", icon: PeopleIcon },
                    ],
                    actions: [
                        {
                            icon: <AddIcon />,
                            title: "Create Template",
                            description: "Create a new shift template with recurrence pattern, time settings, location, and driver requirements.",
                            shortcut: null,
                        },
                        {
                            icon: <VisibilityIcon />,
                            title: "View Template",
                            description: "View template details including all generated shifts, assigned drivers, and template configuration.",
                            shortcut: null,
                        },
                        {
                            icon: <EditIcon />,
                            title: "Edit Template",
                            description: "Modify template settings including recurrence, time, location, and driver assignments.",
                            shortcut: null,
                        },
                        {
                            icon: <DeleteIcon />,
                            title: "Delete Template",
                            description: "Remove a template. Cannot delete templates that have generated shifts still in use. Existing shifts remain unaffected.",
                            shortcut: null,
                        },
                        {
                            icon: <PlayIcon />,
                            title: "Activate/Deactivate Template",
                            description: "Toggle template status. Active templates can generate shifts; inactive templates are paused.",
                            shortcut: null,
                        },
                    ],
                },
                {
                    title: "Driver Assignment",
                    content:
                        "Driver Assignment provides a dedicated interface for assigning drivers to shifts and managing assignments. It features a shift selector, driver search and filtering, multi-select capabilities, and real-time assignment status tracking. This section ensures efficient driver allocation while respecting shift capacity limits.",
                    color: "green",
                    icon: AssignmentIcon,
                    tags: [
                        "Select a shift from the dropdown to view its current assignments and available slots. Only draft and active shifts are shown.",
                        "Use the driver search bar to quickly find drivers by name or mobile number. Search is debounced for performance.",
                        "Select multiple drivers using checkboxes, then click 'Assign Drivers' to bulk assign them to the selected shift.",
                        "The system prevents assigning more drivers than the shift's required capacity. You'll see available slots count.",
                        "You cannot unassign drivers who have checked in or completed their shift. Only pending/confirmed assignments can be removed.",
                        "Shift template information is displayed in the shift details card, showing both template ID and name when available.",
                        "Template information appears in the shift selector dropdown and shift details panel for easy identification of shift patterns."
                    ],
                    subsections: [
                        { title: "Shift Selection", description: "Choose a shift from the searchable dropdown. The interface shows shift details including assigned drivers count, available slots, and template information.", icon: ScheduleIcon },
                        { title: "Driver Search", description: "Search drivers by name or phone number with real-time filtering. Results update as you type (debounced).", icon: SearchIcon },
                        { title: "Multi-Select", description: "Select multiple drivers using checkboxes. Selected drivers are highlighted and shown in a summary before assignment.", icon: PeopleIcon },
                        { title: "Assignment List", description: "View all current assignments for the selected shift with driver details, assignment status, and unassign options.", icon: AssignmentIcon },
                        { title: "Template Information", description: "View template ID and name associated with each shift in the shift details panel for better shift pattern identification.", icon: TemplatesIcon },
                    ],
                    actions: [
                        {
                            icon: <PeopleIcon />,
                            title: "Assign Drivers",
                            description: "Bulk assign selected drivers to the chosen shift. Validates capacity limits and shows success/failure counts.",
                            shortcut: null,
                        },
                        {
                            icon: <DeleteIcon />,
                            title: "Unassign Driver",
                            description: "Remove a driver from a shift assignment. Only works for pending/confirmed assignments, not checked-in or completed ones.",
                            shortcut: null,
                        },
                        {
                            icon: <SearchIcon />,
                            title: "Search Drivers",
                            description: "Search and filter drivers by name or mobile number. Results update in real-time as you type.",
                            shortcut: null,
                        },
                        {
                            icon: <FilterIcon />,
                            title: "Filter by Status",
                            description: "Filter drivers by their current status: Online, Busy, or Idle for better assignment decisions.",
                            shortcut: null,
                        },
                        {
                            icon: <TemplatesIcon />,
                            title: "Template Display",
                            description: "View template information (ID and name) for each shift in the shift details panel. Template info also appears in the shift selector dropdown.",
                            shortcut: null,
                        },
                    ],
                },
                {
                    title: "Live Monitor",
                    content:
                        "Live Monitor provides real-time tracking and visualization of drivers on active shifts. It features an interactive map showing driver locations, status indicators, and assignment information. This section is essential for operational oversight and quick response to field situations.",
                    color: "indigo",
                    icon: MonitorIcon,
                    tags: [
                        "The map displays all drivers assigned to active shifts with color-coded markers indicating their status (Online/Busy/Idle).",
                        "Click on driver markers to see detailed information including name, phone, current location coordinates, and assigned shift details.",
                        "Filter the map view by selecting specific shifts or shift templates from the dropdown. Only drivers assigned to selected shifts are shown.",
                        "Driver locations update in real-time, providing live operational awareness for shift supervisors and managers.",
                        "Filter shifts by template name to quickly view all shifts generated from a specific template pattern.",
                        "All filter inputs and dropdowns have consistent rounded corners for a polished, uniform interface design."
                    ],
                    subsections: [
                        { title: "Map View", description: "Interactive Leaflet map showing driver locations with zoom, pan, and marker click interactions.", icon: LocationIcon },
                        { title: "Status Indicators", description: "Color-coded markers: Green (Online), Amber (Busy), Blue (Idle) for quick status identification.", icon: CheckCircleIcon },
                        { title: "Shift Filtering", description: "Filter map display by selecting specific active shifts or shift templates. Only shows drivers assigned to selected shifts.", icon: FilterIcon },
                        { title: "Template Filter", description: "Filter shifts by template to view all shifts generated from a specific template pattern for better organization.", icon: TemplatesIcon },
                        { title: "Driver Details", description: "Click markers to view driver information, location coordinates, assignment status, and shift details in popups.", icon: InfoIcon },
                    ],
                    actions: [
                        {
                            icon: <MonitorIcon />,
                            title: "Live Tracking",
                            description: "Real-time map showing all drivers on active shifts with their current locations and status updates.",
                            shortcut: null,
                        },
                        {
                            icon: <LocationIcon />,
                            title: "Driver Markers",
                            description: "Interactive markers on the map representing drivers. Click to view details and assignment information.",
                            shortcut: null,
                        },
                        {
                            icon: <ScheduleIcon />,
                            title: "Shift Filter",
                            description: "Select specific shifts from the dropdown to filter which drivers are displayed on the map.",
                            shortcut: null,
                        },
                        {
                            icon: <TemplatesIcon />,
                            title: "Template Filter",
                            description: "Filter shifts by template name to view all shifts generated from a specific template pattern. Helps organize and monitor template-based shifts.",
                            shortcut: null,
                        },
                    ],
                },
                {
                    title: "Compliance Reports",
                    content:
                        "Compliance Reports provides detailed analytics and compliance tracking for driver shift performance. It tracks metrics such as online duration, acceptance rates, orders handled, and compliance status. This section helps ensure drivers meet shift requirements and maintain service quality standards.",
                    color: "orange",
                    icon: AssessmentIcon,
                    tags: [
                        "Select a driver from the searchable dropdown to view their complete shift history and compliance metrics.",
                        "Compliance is calculated based on two criteria: online duration (must be ≥95% of shift duration) and acceptance rate (must be ≥90%).",
                        "Reports show detailed metrics including online hours, busy hours, idle hours, orders accepted/rejected, and acceptance rate percentage.",
                        "Use date range filters to analyze driver performance over specific time periods for better insights.",
                        "Aggregated statistics show total hours, average acceptance rate, and compliant shifts count for comprehensive analysis.",
                        "Template information is prominently displayed for each shift, showing both template ID and name in a highlighted badge format (ID: xxxxxxxx • Template: Template Name).",
                        "Filter state is automatically saved and restored when navigating back from report details, maintaining your search criteria and selected driver.",
                        "The reports table automatically adjusts its height to fit content without scrolling, providing a cleaner viewing experience."
                    ],
                    subsections: [
                        { title: "Driver Selection", description: "Search and select a driver to view their shift history. Supports search by name or mobile number. Selection persists when navigating back from details.", icon: PeopleIcon },
                        { title: "Date Range", description: "Filter reports by start and end dates to analyze performance over specific time periods. Filter state is saved automatically.", icon: CalendarIcon },
                        { title: "Compliance Metrics", description: "Track online duration percentage, acceptance rate, orders handled, and overall compliance status per shift.", icon: TrendingUpIcon },
                        { title: "Aggregated Stats", description: "View total online/busy/idle hours, average acceptance rate, and count of compliant shifts.", icon: AssessmentIcon },
                        { title: "Template Display", description: "Each shift shows its associated template with ID and name clearly highlighted in a badge format for easy identification.", icon: TemplatesIcon },
                        { title: "State Persistence", description: "Your selected driver, date filters, and current page are automatically saved and restored when navigating between views.", icon: CheckCircleIcon },
                    ],
                    actions: [
                        {
                            icon: <AssessmentIcon />,
                            title: "View Reports",
                            description: "Display detailed compliance reports for selected drivers showing shift-by-shift performance metrics.",
                            shortcut: null,
                        },
                        {
                            icon: <TrendingUpIcon />,
                            title: "Compliance Status",
                            description: "See which shifts were compliant (green) or non-compliant (red) based on online duration and acceptance rate thresholds.",
                            shortcut: null,
                        },
                        {
                            icon: <CalendarIcon />,
                            title: "Date Range Filter",
                            description: "Set start and end dates to filter shift history and analyze performance over specific periods.",
                            shortcut: null,
                        },
                        {
                            icon: <AccessTimeIcon />,
                            title: "Time Metrics",
                            description: "View detailed time breakdown: online hours, busy hours, idle hours, and online percentage per shift.",
                            shortcut: null,
                        },
                        {
                            icon: <TemplatesIcon />,
                            title: "Template Information",
                            description: "Each shift displays its associated template with ID and name in a highlighted badge format (ID: xxxxxxxx • Template: Template Name) for easy identification.",
                            shortcut: null,
                        },
                        {
                            icon: <CheckCircleIcon />,
                            title: "State Persistence",
                            description: "Your selected driver, date filters, and current page are automatically saved. When you navigate back from report details, all filters and selections are restored.",
                            shortcut: null,
                        },
                    ],
                },
                {
                    title: "Shift Statuses & Lifecycle",
                    content:
                        "Understanding shift statuses and their transitions is crucial for effective shift management. Shifts progress through different states from creation to completion, each with specific characteristics and available actions.",
                    color: "blue",
                    icon: ScheduleIcon,
                    subsections: [
                        { title: "Draft", description: "Initial state when a shift is created. Can be edited, activated, or deleted. Not yet available for driver assignment.", icon: EditIcon },
                        { title: "Active", description: "Shift is live and operational. Drivers can be assigned, checked in, and complete their work. Can be cancelled if needed.", icon: PlayIcon },
                        { title: "Completed", description: "Shift has ended and all work is finished. View-only status for historical records and reporting.", icon: CheckCircleIcon },
                        { title: "Cancelled", description: "Shift was cancelled before or during operation. All assignments are cancelled. Cannot be reactivated.", icon: StopIcon },
                    ],
                },
                {
                    title: "Assignment Statuses",
                    content:
                        "Driver assignments have their own status lifecycle that tracks the progression from assignment to completion. Understanding these statuses helps manage driver availability and shift fulfillment.",
                    color: "purple",
                    icon: AssignmentIcon,
                    subsections: [
                        { title: "Pending", description: "Initial assignment status. Driver has been assigned but hasn't confirmed or checked in yet.", icon: ScheduleIcon },
                        { title: "Confirmed", description: "Driver has confirmed their assignment and is expected to work the shift.", icon: CheckCircleIcon },
                        { title: "Checked In", description: "Driver has checked in and started working. Cannot be unassigned at this point.", icon: PlayIcon },
                        { title: "Completed", description: "Driver has finished their shift work. Assignment is complete and cannot be modified.", icon: CheckCircleIcon },
                        { title: "Cancelled", description: "Assignment was cancelled before completion. Driver is no longer assigned to this shift.", icon: StopIcon },
                        { title: "No Show", description: "Driver did not show up for their assigned shift despite confirmation.", icon: StopIcon },
                    ],
                },
            ],
        },
        ar: {
            dir: "rtl",
            title: "توثيق إدارة الورديات",
            subtitle: "دليل شامل لجدولة وتعيين ومراقبة ورديات السائقين",
            buttonText: "English",
            searchPlaceholder: "البحث في التوثيق...",
            tableHeaders: {
                icon: "أيقونة",
                action: "الإجراء",
                description: "الوصف"
            },
            sections: [
                {
                    title: "نظرة عامة",
                    content:
                        "وحدة إدارة الورديات هي نظام شامل ضمن بوابة برق الإدارية مصمم لجدولة وتعيين ومراقبة ورديات السائقين. يوفر منصة مركزية لإدارة جداول الورديات، وتعيين السائقين للورديات، ومراقبة نشاط السائقين في الوقت الفعلي، وتتبع الامتثال، وإنشاء قوالب ورديات قابلة لإعادة الاستخدام. تنظم الواجهة في خمسة أقسام رئيسية: جلسة الوردية، وجدولة الورديات، وتعيين السائقين، والمراقبة المباشرة، وتقارير الامتثال، حيث يقدم كل منها أدوات متخصصة لإدارة الورديات بكفاءة.",
                    color: "blue",
                    icon: BookIcon,
                },
                {
                    title: "جلسة الوردية",
                    content:
                        "جلسة الوردية هي الواجهة الأساسية لإدارة جداول الورديات الفردية. تتيح لك إنشاء وعرض وتعديل وتفعيل وإلغاء الورديات. تمثل كل وردية فترة عمل مجدولة مع فترات زمنية محددة ومتطلبات موقع واحتياجات سعة السائقين. تدعم الواجهة كل من عرض البطاقات وعرض الجدول لتصور البيانات المرنة.",
                    color: "blue",
                    icon: ScheduleIcon,
                    tags: [
                        "تعرض بطاقات الوردية معلومات رئيسية تشمل أوقات البدء/الانتهاء، وعدد السائقين المطلوبين، والسائقين المعينين، والفتحات المتاحة، والحالة (مسودة/نشطة/مكتملة/ملغاة)، وتفاصيل الموقع.",
                        "يمكن تفعيل الورديات المسودة لجعلها متاحة لتعيين السائقين. يمكن إلغاء الورديات النشطة إذا لزم الأمر.",
                        "تعرض بطاقات الإحصائيات إجمالي الورديات، والورديات النشطة، والورديات المسودة، والورديات المكتملة في لمحة سريعة للنظرة العامة.",
                        "تسمح الفلاتر بتضييق نطاق الورديات حسب المدينة والحي والحالة ونطاق التاريخ ومصطلحات البحث للإدارة الفعالة."
                    ],
                    subsections: [
                        { title: "أوضاع العرض", description: "التبديل بين عرض البطاقات (البطاقات المرئية) وعرض الجدول (تنسيق جدول البيانات التفصيلي) باستخدام أزرار وضع العرض.", icon: GridViewIcon },
                        { title: "الفلاتر", description: "تصفية الورديات حسب المدينة والحي والحالة (مسودة/نشطة/مكتملة/ملغاة) ونطاق التاريخ وكلمات البحث.", icon: FilterIcon },
                        { title: "ترقيم الصفحات", description: "التنقل عبر صفحات متعددة من الورديات مع 12 عنصرًا لكل صفحة. يتم حفظ حالة الصفحة في تخزين المتصفح.", icon: ScheduleIcon },
                        { title: "الإجراءات", description: "انقر على بطاقات الورديات لعرض التفاصيل أو تعديل الورديات أو تفعيل الورديات المسودة أو إلغاء الورديات النشطة.", icon: EditIcon },
                    ],
                    actions: [
                        { icon: <VisibilityIcon />, title: "عرض تفاصيل الوردية", description: "يفتح عرضًا تفصيليًا للوردية المحددة يعرض جميع التعيينات وإحصائيات السائقين ومعلومات الامتثال.", shortcut: null },
                        { icon: <EditIcon />, title: "تعديل الوردية", description: "يسمح بتعديل تفاصيل الوردية مثل الوقت والسائقين المطلوبين وإعدادات الموقع قبل التفعيل.", shortcut: null },
                        { icon: <PlayIcon />, title: "تفعيل الوردية", description: "يحول وردية مسودة إلى حالة نشطة، مما يجعلها متاحة لتعيين السائقين والاستخدام التشغيلي.", shortcut: null },
                        { icon: <StopIcon />, title: "إلغاء الوردية", description: "يلغي وردية نشطة وجميع التعيينات المعلقة/المؤكدة. لا يمكن إلغاء الورديات التي لديها سائقون قاموا بتسجيل الدخول أو أكملوا.", shortcut: null },
                        { icon: <GridViewIcon />, title: "عرض البطاقات", description: "يعرض الورديات كبطاقات مرئية مع معلومات رئيسية مرئية في لمحة. أفضل للمسح السريع والنظرة العامة.", shortcut: null },
                        { icon: <ListViewIcon />, title: "عرض الجدول", description: "يعرض الورديات بتنسيق جدول تفصيلي مع أعمدة قابلة للفرز. أفضل للتحليل التفصيلي والمقارنة.", shortcut: null },
                    ],
                },
                {
                    title: "جدولة الورديات",
                    content:
                        "تمكنك جدولة الورديات من إنشاء أنماط ورديات قابلة لإعادة الاستخدام يمكنها توليد ورديات متعددة تلقائيًا. تدعم القوالب أنواع تكرار مختلفة بما في ذلك يومي وأسبوعي وتواريخ محددة ونطاقات تواريخ مخصصة. هذه الميزة مثالية لإدارة الجداول المتكررة وتقليل جهد إنشاء الورديات اليدوي.",
                    color: "purple",
                    icon: TemplatesIcon,
                    tags: [
                        "يمكن تعيين القوالب إلى حالة نشطة أو غير نشطة. فقط القوالب النشطة يمكنها توليد ورديات تلقائيًا.",
                        "تسمح القوالب الأسبوعية بتحديد أيام الأسبوع التي يجب أن تحدث فيها الوردية (مثل الاثنين والأربعاء والجمعة).",
                        "يمكن أن تحتوي القوالب على تواريخ صالحة من/حتى للتحكم في وقت توليد الورديات، واستبعاد تواريخ محددة.",
                        "يمكنك تعيين سائقين محددين للقوالب، وسيتم تعيينهم تلقائيًا عند توليد الورديات من القالب."
                    ],
                    subsections: [
                        { title: "أنواع التكرار", description: "اختر من أنماط التكرار اليومية أو الأسبوعية أو التاريخ المحدد أو النطاق المخصص للجدولة المرنة.", icon: CalendarIcon },
                        { title: "حالة القالب", description: "تفعيل أو إلغاء تفعيل القوالب. لن تولد القوالب غير النشطة ورديات جديدة لكن الورديات الموجودة تبقى.", icon: CheckCircleIcon },
                        { title: "تعيين السائقين", description: "تعيين سائقين مسبقًا للقوالب حتى يتم تعيينهم تلقائيًا عند توليد الورديات.", icon: PeopleIcon },
                    ],
                    actions: [
                        { icon: <AddIcon />, title: "إنشاء قالب", description: "إنشاء قالب وردية جديد مع نمط التكرار وإعدادات الوقت والموقع ومتطلبات السائقين.", shortcut: null },
                        { icon: <VisibilityIcon />, title: "عرض القالب", description: "عرض تفاصيل القالب بما في ذلك جميع الورديات المولدة والسائقين المعينين وتكوين القالب.", shortcut: null },
                        { icon: <EditIcon />, title: "تعديل القالب", description: "تعديل إعدادات القالب بما في ذلك التكرار والوقت والموقع وتعيينات السائقين.", shortcut: null },
                        { icon: <DeleteIcon />, title: "حذف القالب", description: "إزالة قالب. لا يمكن حذف القوالب التي لديها ورديات مولدة لا تزال قيد الاستخدام. تبقى الورديات الموجودة غير متأثرة.", shortcut: null },
                        { icon: <PlayIcon />, title: "تفعيل/إلغاء تفعيل القالب", description: "تبديل حالة القالب. يمكن للقوالب النشطة توليد ورديات؛ القوالب غير النشطة متوقفة.", shortcut: null },
                    ],
                },
                {
                    title: "تعيين السائقين",
                    content:
                        "يوفر تعيين السائقين واجهة مخصصة لتعيين السائقين للورديات وإدارة التعيينات. تتميز بمحدد الوردية، وبحث السائقين والتصفية، وإمكانيات الاختيار المتعدد، وتتبع حالة التعيين في الوقت الفعلي. تضمن هذه الواجهة تخصيص السائقين بكفاءة مع احترام حدود سعة الوردية.",
                    color: "green",
                    icon: AssignmentIcon,
                    tags: [
                        "حدد وردية من القائمة المنسدلة لعرض تعييناتها الحالية والفتحات المتاحة. يتم عرض الورديات المسودة والنشطة فقط.",
                        "استخدم شريط بحث السائقين للعثور بسرعة على السائقين بالاسم أو رقم الجوال. البحث متأخر للأداء.",
                        "حدد عدة سائقين باستخدام مربعات الاختيار، ثم انقر على 'تعيين السائقين' لتعيينهم بشكل جماعي للوردية المحددة.",
                        "يمنع النظام تعيين سائقين أكثر من السعة المطلوبة للوردية. سترى عدد الفتحات المتاحة.",
                        "لا يمكنك إلغاء تعيين السائقين الذين قاموا بتسجيل الدخول أو أكملوا ورديتهم. يمكن إزالة التعيينات المعلقة/المؤكدة فقط.",
                        "يتم عرض معلومات قالب الوردية في بطاقة تفاصيل الوردية، مع إظهار معرف القالب والاسم عند التوفر.",
                        "تظهر معلومات القالب في القائمة المنسدلة لمحدد الوردية ولوحة تفاصيل الوردية لتسهيل التعرف على أنماط الورديات."
                    ],
                    subsections: [
                        { title: "اختيار الوردية", description: "اختر وردية من القائمة المنسدلة القابلة للبحث. تعرض الواجهة تفاصيل الوردية بما في ذلك عدد السائقين المعينين والفتحات المتاحة ومعلومات القالب.", icon: ScheduleIcon },
                        { title: "بحث السائقين", description: "البحث عن السائقين بالاسم أو رقم الهاتف مع التصفية في الوقت الفعلي. تتحدث النتائج أثناء الكتابة (متأخر).", icon: SearchIcon },
                        { title: "الاختيار المتعدد", description: "حدد عدة سائقين باستخدام مربعات الاختيار. يتم تمييز السائقين المحددين وعرضهم في ملخص قبل التعيين.", icon: PeopleIcon },
                        { title: "قائمة التعيينات", description: "عرض جميع التعيينات الحالية للوردية المحددة مع تفاصيل السائقين وحالة التعيين وخيارات إلغاء التعيين.", icon: AssignmentIcon },
                        { title: "معلومات القالب", description: "عرض معرف القالب والاسم المرتبط بكل وردية في لوحة تفاصيل الوردية لتسهيل التعرف على نمط الوردية.", icon: TemplatesIcon },
                    ],
                    actions: [
                        { icon: <PeopleIcon />, title: "تعيين السائقين", description: "تعيين السائقين المحددين بشكل جماعي للوردية المختارة. يتحقق من حدود السعة ويعرض أعداد النجاح/الفشل.", shortcut: null },
                        { icon: <DeleteIcon />, title: "إلغاء تعيين السائق", description: "إزالة سائق من تعيين وردية. يعمل فقط للتعيينات المعلقة/المؤكدة، وليس للذين قاموا بتسجيل الدخول أو أكملوا.", shortcut: null },
                        { icon: <SearchIcon />, title: "بحث السائقين", description: "البحث عن السائقين والتصفية بالاسم أو رقم الجوال. تتحدث النتائج في الوقت الفعلي أثناء الكتابة.", shortcut: null },
                        { icon: <FilterIcon />, title: "التصفية حسب الحالة", description: "تصفية السائقين حسب حالتهم الحالية: متصل أو مشغول أو خامل لاتخاذ قرارات تعيين أفضل.", shortcut: null },
                        { icon: <TemplatesIcon />, title: "عرض القالب", description: "عرض معلومات القالب (المعرف والاسم) لكل وردية في لوحة تفاصيل الوردية. تظهر معلومات القالب أيضًا في القائمة المنسدلة لمحدد الوردية.", shortcut: null },
                    ],
                },
                {
                    title: "المراقبة المباشرة",
                    content:
                        "توفر المراقبة المباشرة تتبعًا وتصورًا في الوقت الفعلي للسائقين في الورديات النشطة. تتميز بخريطة تفاعلية تعرض مواقع السائقين ومؤشرات الحالة ومعلومات التعيين. هذه الواجهة ضرورية للإشراف التشغيلي والاستجابة السريعة لحالات الميدان.",
                    color: "indigo",
                    icon: MonitorIcon,
                    tags: [
                        "تعرض الخريطة جميع السائقين المعينين للورديات النشطة مع علامات ملونة تشير إلى حالتهم (متصل/مشغول/خامل).",
                        "انقر على علامات السائقين لرؤية معلومات تفصيلية تشمل الاسم والهاتف وإحداثيات الموقع الحالي وتفاصيل الوردية المعينة.",
                        "قم بتصفية عرض الخريطة عن طريق تحديد ورديات محددة أو قوالب ورديات من القائمة المنسدلة. يتم عرض السائقين المعينين للورديات المحددة فقط.",
                        "تتحدث مواقع السائقين في الوقت الفعلي، مما يوفر وعيًا تشغيليًا مباشرًا لمشرفي الورديات والمديرين.",
                        "قم بتصفية الورديات حسب اسم القالب لعرض جميع الورديات المولدة من نمط قالب محدد بسرعة.",
                        "جميع مدخلات الفلتر والقوائم المنسدلة لها زوايا دائرية متسقة لتصميم واجهة موحد ومصقول."
                    ],
                    subsections: [
                        { title: "عرض الخريطة", description: "خريطة Leaflet تفاعلية تعرض مواقع السائقين مع تكبير وتصغير ونقر على العلامات.", icon: LocationIcon },
                        { title: "مؤشرات الحالة", description: "علامات ملونة: أخضر (متصل)، كهرماني (مشغول)، أزرق (خامل) لتحديد الحالة السريع.", icon: CheckCircleIcon },
                        { title: "تصفية الورديات", description: "تصفية عرض الخريطة عن طريق تحديد ورديات نشطة محددة أو قوالب ورديات. يعرض فقط السائقين المعينين للورديات المحددة.", icon: FilterIcon },
                        { title: "فلتر القالب", description: "تصفية الورديات حسب القالب لعرض جميع الورديات المولدة من نمط قالب محدد لتنظيم أفضل.", icon: TemplatesIcon },
                        { title: "تفاصيل السائق", description: "انقر على العلامات لعرض معلومات السائق وإحداثيات الموقع وحالة التعيين وتفاصيل الوردية في النوافذ المنبثقة.", icon: InfoIcon },
                    ],
                    actions: [
                        { icon: <MonitorIcon />, title: "التتبع المباشر", description: "خريطة في الوقت الفعلي تعرض جميع السائقين في الورديات النشطة مع مواقعهم الحالية وتحديثات الحالة.", shortcut: null },
                        { icon: <LocationIcon />, title: "علامات السائقين", description: "علامات تفاعلية على الخريطة تمثل السائقين. انقر لعرض التفاصيل ومعلومات التعيين.", shortcut: null },
                        { icon: <ScheduleIcon />, title: "فلتر الورديات", description: "حدد ورديات محددة من القائمة المنسدلة لتصفية السائقين المعروضين على الخريطة.", shortcut: null },
                        { icon: <TemplatesIcon />, title: "فلتر القالب", description: "تصفية الورديات حسب اسم القالب لعرض جميع الورديات المولدة من نمط قالب محدد. يساعد في تنظيم ومراقبة الورديات القائمة على القوالب.", shortcut: null },
                    ],
                },
                {
                    title: "تقارير الامتثال",
                    content:
                        "توفر تقارير الامتثال تحليلات تفصيلية وتتبع الامتثال لأداء ورديات السائقين. تتتبع مقاييس مثل مدة الاتصال ومعدلات القبول والطلبات المعالجة وحالة الامتثال. تساعد هذه الواجهة في ضمان تلبية السائقين لمتطلبات الوردية والحفاظ على معايير جودة الخدمة.",
                    color: "orange",
                    icon: AssessmentIcon,
                    tags: [
                        "حدد سائقًا من القائمة المنسدلة القابلة للبحث لعرض تاريخ وردياته الكامل ومقاييس الامتثال.",
                        "يتم حساب الامتثال بناءً على معيارين: مدة الاتصال (يجب أن تكون ≥95% من مدة الوردية) ومعدل القبول (يجب أن يكون ≥90%).",
                        "تعرض التقارير مقاييس تفصيلية تشمل ساعات الاتصال وساعات الانشغال وساعات الخمول والطلبات المقبولة/المرفوضة ونسبة معدل القبول.",
                        "استخدم فلاتر نطاق التاريخ لتحليل أداء السائقين على فترات زمنية محددة للحصول على رؤى أفضل.",
                        "تعرض الإحصائيات المجمعة إجمالي الساعات ومتوسط معدل القبول وعدد الورديات الممتثلة للتحليل الشامل.",
                        "يتم عرض معلومات القالب بشكل بارز لكل وردية، مع إظهار معرف القالب والاسم بتنسيق شارة مميزة (ID: xxxxxxxx • Template: Template Name).",
                        "يتم حفظ حالة الفلتر تلقائيًا واستعادتها عند التنقل للخلف من تفاصيل التقرير، مما يحافظ على معايير البحث والسائق المحدد.",
                        "يضبط جدول التقارير ارتفاعه تلقائيًا ليتناسب مع المحتوى دون تمرير، مما يوفر تجربة عرض أنظف."
                    ],
                    subsections: [
                        { title: "اختيار السائق", description: "البحث عن سائق وتحديده لعرض تاريخ وردياته. يدعم البحث بالاسم أو رقم الجوال. يتم الحفاظ على التحديد عند التنقل للخلف من التفاصيل.", icon: PeopleIcon },
                        { title: "نطاق التاريخ", description: "تصفية التقارير حسب تواريخ البدء والانتهاء لتحليل الأداء على فترات زمنية محددة. يتم حفظ حالة الفلتر تلقائيًا.", icon: CalendarIcon },
                        { title: "مقاييس الامتثال", description: "تتبع نسبة مدة الاتصال ومعدل القبول والطلبات المعالجة وحالة الامتثال الإجمالية لكل وردية.", icon: TrendingUpIcon },
                        { title: "الإحصائيات المجمعة", description: "عرض إجمالي ساعات الاتصال/الانشغال/الخمول ومتوسط معدل القبول وعدد الورديات الممتثلة.", icon: AssessmentIcon },
                        { title: "عرض القالب", description: "تعرض كل وردية القالب المرتبط بها مع المعرف والاسم بشكل واضح في تنسيق شارة لتسهيل التعرف.", icon: TemplatesIcon },
                        { title: "استمرارية الحالة", description: "يتم حفظ السائق المحدد وفلاتر التاريخ والصفحة الحالية تلقائيًا واستعادتها عند التنقل بين العروض.", icon: CheckCircleIcon },
                    ],
                    actions: [
                        { icon: <AssessmentIcon />, title: "عرض التقارير", description: "عرض تقارير الامتثال التفصيلية للسائقين المحددين مع مقاييس الأداء لكل وردية.", shortcut: null },
                        { icon: <TrendingUpIcon />, title: "حالة الامتثال", description: "رؤية الورديات التي كانت متوافقة (أخضر) أو غير متوافقة (أحمر) بناءً على مدة الاتصال وعتبات معدل القبول.", shortcut: null },
                        { icon: <CalendarIcon />, title: "فلتر نطاق التاريخ", description: "تعيين تواريخ البدء والانتهاء لتصفية تاريخ الورديات وتحليل الأداء على فترات محددة.", shortcut: null },
                        { icon: <AccessTimeIcon />, title: "مقاييس الوقت", description: "عرض تفصيل الوقت: ساعات الاتصال وساعات الانشغال وساعات الخمول ونسبة الاتصال لكل وردية.", shortcut: null },
                        { icon: <TemplatesIcon />, title: "معلومات القالب", description: "تعرض كل وردية القالب المرتبط بها مع المعرف والاسم بتنسيق شارة مميزة (ID: xxxxxxxx • Template: Template Name) لتسهيل التعرف.", shortcut: null },
                        { icon: <CheckCircleIcon />, title: "استمرارية الحالة", description: "يتم حفظ السائق المحدد وفلاتر التاريخ والصفحة الحالية تلقائيًا. عند التنقل للخلف من تفاصيل التقرير، يتم استعادة جميع الفلاتر والتحديدات.", shortcut: null },
                    ],
                },
                {
                    title: "حالات الورديات ودورة الحياة",
                    content:
                        "فهم حالات الورديات وتحولاتها أمر بالغ الأهمية لإدارة الورديات الفعالة. تتقدم الورديات عبر حالات مختلفة من الإنشاء إلى الإكمال، لكل منها خصائص محددة وإجراءات متاحة.",
                    color: "blue",
                    icon: ScheduleIcon,
                    subsections: [
                        { title: "مسودة", description: "الحالة الأولية عند إنشاء وردية. يمكن تعديلها أو تفعيلها أو حذفها. غير متاحة بعد لتعيين السائقين.", icon: EditIcon },
                        { title: "نشطة", description: "الوردية حية وتشغيلية. يمكن تعيين السائقين وتسجيل الدخول وإكمال عملهم. يمكن إلغاؤها إذا لزم الأمر.", icon: PlayIcon },
                        { title: "مكتملة", description: "انتهت الوردية واكتمل كل العمل. حالة عرض فقط للسجلات التاريخية والتقارير.", icon: CheckCircleIcon },
                        { title: "ملغاة", description: "تم إلغاء الوردية قبل أو أثناء التشغيل. يتم إلغاء جميع التعيينات. لا يمكن إعادة تفعيلها.", icon: StopIcon },
                    ],
                },
                {
                    title: "حالات التعيين",
                    content:
                        "لتعيينات السائقين دورة حياة حالة خاصة بها تتتبع التقدم من التعيين إلى الإكمال. يساعد فهم هذه الحالات في إدارة توفر السائقين وإتمام الورديات.",
                    color: "purple",
                    icon: AssignmentIcon,
                    subsections: [
                        { title: "معلق", description: "حالة التعيين الأولية. تم تعيين السائق لكنه لم يؤكد أو يسجل الدخول بعد.", icon: ScheduleIcon },
                        { title: "مؤكد", description: "أكد السائق تعيينه ومن المتوقع أن يعمل الوردية.", icon: CheckCircleIcon },
                        { title: "تم تسجيل الدخول", description: "سجل السائق الدخول وبدأ العمل. لا يمكن إلغاء التعيين في هذه المرحلة.", icon: PlayIcon },
                        { title: "مكتمل", description: "أكمل السائق عمل ورديته. التعيين مكتمل ولا يمكن تعديله.", icon: CheckCircleIcon },
                        { title: "ملغى", description: "تم إلغاء التعيين قبل الإكمال. السائق لم يعد معينًا لهذه الوردية.", icon: StopIcon },
                        { title: "عدم الحضور", description: "لم يحضر السائق لورديته المعينة رغم التأكيد.", icon: StopIcon },
                    ],
                },
            ],
        }
    };
    const currentDoc = docContent[lang];

    const getColorClasses = (color) => {
        const colors = {
            blue: {
                gradient: "from-primaryColor via-primaryColor to-darkBlue",
                iconGradient: "from-primaryColor to-darkBlue",
                bg: "bg-blue-50/90",
                border: "border-blue-200/60",
                text: "text-blue-700",
                lightBg: "bg-blue-50/50",
                accent: "bg-blue-100/80",
            },
            purple: {
                gradient: "from-purple-500 via-purple-600 to-purple-700",
                iconGradient: "from-purple-500 to-purple-700",
                bg: "bg-purple-50/90",
                border: "border-purple-200/60",
                text: "text-purple-700",
                lightBg: "bg-purple-50/50",
                accent: "bg-purple-100/80",
            },
            green: {
                gradient: "from-successColor via-green-600 to-green-700",
                iconGradient: "from-successColor to-green-600",
                bg: "bg-green-50/90",
                border: "border-green-200/60",
                text: "text-green-700",
                lightBg: "bg-green-50/50",
                accent: "bg-green-100/80",
            },
            orange: {
                gradient: "from-warningColor via-orange-600 to-orange-700",
                iconGradient: "from-warningColor to-orange-600",
                bg: "bg-orange-50/90",
                border: "border-orange-200/60",
                text: "text-orange-700",
                lightBg: "bg-orange-50/50",
                accent: "bg-orange-100/80",
            },
            indigo: {
                gradient: "from-indigo-500 via-indigo-600 to-indigo-700",
                iconGradient: "from-indigo-500 to-indigo-700",
                bg: "bg-indigo-50/90",
                border: "border-indigo-200/60",
                text: "text-indigo-700",
                lightBg: "bg-indigo-50/50",
                accent: "bg-indigo-100/80",
            },
        };
        return colors[color] || colors.blue;
    };

    const filteredSections = currentDoc.sections.filter((section) => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            section.title.toLowerCase().includes(query) ||
            section.content.toLowerCase().includes(query) ||
            section.tags?.some((tag) => tag.toLowerCase().includes(query)) ||
            section.subsections?.some((sub) => sub.title.toLowerCase().includes(query) || sub.description.toLowerCase().includes(query)) ||
            section.actions?.some((action) => action.title.toLowerCase().includes(query) || action.description.toLowerCase().includes(query))
        );
    });

    return (
        <>
            {showButton && (
                <button
                    onClick={() => {
                        if (externalOpenD === undefined) {
                            setInternalOpenD(true);
                        }
                    }}
                    className="bg-white p-2 rounded-lg border border-gray-300 shadow-sm hover:shadow-md hover:bg-gray-50 transition-all duration-200 cursor-pointer flex items-center justify-center"
                    title="Open Documentation"
                    aria-label="Open Documentation"
                >
                    <DocsIcon />
                </button>
            )}

            <Dialog 
                fullScreen 
                open={openD} 
                onClose={handleClose} 
                PaperProps={{ 
                    sx: {
                        '& .MuiDialog-paper': {
                            margin: 0,
                            maxWidth: '100%',
                            height: '100%',
                            backgroundColor: 'transparent',
                        }
                    }
                }}
            >
                <div className="flex flex-col h-screen bg-gray-50 overflow-hidden" dir={currentDoc.dir}>
                    {/* Top Bar */}
                    <div className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-20">
                        <div className="px-6 py-4 flex items-center justify-between gap-4">
                            {/* Back Button */}
                            <button
                                onClick={handleClose}
                                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-gray-700 hover:text-primaryColor hover:bg-gray-50 transition-all duration-200 ${currentDoc.dir === "rtl" ? "flex-row-reverse" : ""}`}
                            >
                                <ArrowBackIcon sx={{ fontSize: '20px' }} />
                                <span className="text-sm font-medium">Back</span>
                            </button>
                            
                            {/* Search */}
                            <div className="flex-1 max-w-2xl">
                                <div className="relative">
                                    <SearchIcon sx={{ fontSize: '18px', color: '#9CA3AF', position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', zIndex: 1 }} />
                                    <input
                                        type="text"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        placeholder={currentDoc.searchPlaceholder}
                                        className={`w-full pl-11 pr-10 py-2.5 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-primaryColor/20 focus:border-primaryColor outline-none transition-all bg-gray-50 hover:bg-white focus:bg-white ${currentDoc.dir === "rtl" ? "text-right pr-11 pl-10" : ""}`}
                                    />
                                    {searchQuery && (
                                        <button
                                            onClick={() => setSearchQuery("")}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-200 rounded-full transition-colors"
                                        >
                                            <CloseIcon sx={{ fontSize: '16px', color: '#6B7280' }} />
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Language Toggle */}
                            <button 
                                onClick={toggleLang} 
                                className="px-4 py-2.5 bg-gradient-to-r from-primaryColor to-darkBlue hover:from-darkBlue hover:to-primaryColor text-white text-sm font-semibold rounded-xl transition-all duration-200 shadow-sm hover:shadow-md whitespace-nowrap"
                            >
                                {currentDoc.buttonText}
                            </button>
                        </div>
                    </div>

                    {/* Scroll Progress Bar */}
                    <div className="fixed top-[73px] left-0 right-0 h-1 bg-gray-200/30 z-40">
                        <div 
                            className="h-full bg-gradient-to-r from-primaryColor to-darkBlue transition-all duration-150"
                            style={{ width: `${scrollProgress}%` }}
                        />
                    </div>

                    {/* Scrollable Content */}
                    <main 
                        ref={scrollContainerRef}
                        className="flex-1 overflow-y-auto"
                        style={{ scrollBehavior: 'smooth' }}
                    >
                        <div className={`max-w-6xl mx-auto ${currentDoc.dir === "ltr" ? "px-4 sm:px-6 lg:px-8" : "px-4 sm:px-6 lg:px-8"} py-8`}>

                                {/* Hero Header */}
                                <div className="mb-8">
                                    <div className="bg-gradient-to-r from-primaryColor via-primaryColor to-darkBlue rounded-2xl p-8 shadow-xl relative overflow-hidden">
                                        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32"></div>
                                        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full -ml-24 -mb-24"></div>
                                        
                                        <div className="relative z-10">
                                            <div className={`flex items-start gap-4 ${currentDoc.dir === "rtl" ? "flex-row-reverse" : ""}`}>
                                                <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-2xl border border-white/30">
                                                    <BookIcon sx={{ fontSize: '32px', color: 'white' }} />
                                                </div>
                                                <div className="flex-1">
                                                    <div className={`flex items-center gap-2 mb-2 ${currentDoc.dir === "rtl" ? "flex-row-reverse" : ""}`}>
                                                        <h1 className="text-3xl font-bold text-white">{currentDoc.title}</h1>
                                                        <Badge variant="secondary" className="bg-white/20 backdrop-blur-sm text-white border border-white/30 px-3 py-1 rounded-full text-xs font-semibold">
                                                            Guide
                                                        </Badge>
                                                    </div>
                                                    <p className="text-base text-white/90 leading-relaxed max-w-2xl">
                                                        {currentDoc.subtitle}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Search Results Info */}
                                {searchQuery && (
                                    <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                        <p className="text-sm text-blue-800">
                                            Found <span className="font-semibold">{filteredSections.length}</span> section{filteredSections.length !== 1 ? 's' : ''} matching "{searchQuery}"
                                        </p>
                                    </div>
                                )}

                                {/* Sections */}
                                <div className="space-y-6">
                                    {filteredSections.map((section, index) => {
                                        const originalIndex = currentDoc.sections.indexOf(section);
                                        const colors = getColorClasses(section.color || "blue");
                                        const SectionIcon = section.icon;
                                        const isExpanded = expandedSections.has(originalIndex);
                                        
                                        return (
                                            <div
                                                key={originalIndex}
                                                ref={(el) => {
                                                    if (el) {
                                                        sectionRefs.current[originalIndex] = el;
                                                    } else {
                                                        delete sectionRefs.current[originalIndex];
                                                    }
                                                }}
                                                className="group"
                                            >
                                                <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
                                                    {/* Section Header - Clickable */}
                                                    <button
                                                        onClick={() => toggleSection(originalIndex)}
                                                        className="w-full p-4 hover:bg-gray-50 transition-colors"
                                                    >
                                                        <div className={`flex items-center gap-3 ${currentDoc.dir === "rtl" ? "flex-row-reverse" : ""}`}>
                                                            <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${colors.iconGradient} flex items-center justify-center flex-shrink-0`}>
                                                                <SectionIcon sx={{ fontSize: '20px', color: 'white' }} />
                                                            </div>
                                                            <div className="flex-1 text-left">
                                                                <h2 className="text-lg font-semibold text-gray-900">{section.title}</h2>
                                                            </div>
                                                            <div className={`transform transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}>
                                                                <ExpandMoreIcon sx={{ fontSize: '20px', color: '#6B7280' }} />
                                                            </div>
                                                        </div>
                                                    </button>

                                                    {/* Expandable Content */}
                                                    {isExpanded && (
                                                        <div className="p-6 border-t border-gray-200 space-y-5">
                                                            <p className="text-gray-700 leading-relaxed text-sm">{section.content}</p>
                                                            
                                                            {/* Tags */}
                                                            {section.tags && (
                                                                <div className="space-y-2">
                                                                    {section.tags.map((tag, tagIndex) => (
                                                                        <div 
                                                                            key={tagIndex}
                                                                            className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200"
                                                                        >
                                                                            <CheckCircleIcon sx={{ fontSize: '16px', color: '#10B981', marginTop: '2px', flexShrink: 0 }} />
                                                                            <p className="text-sm text-gray-700 leading-relaxed flex-1">{tag}</p>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}

                                                            {/* Subsections */}
                                                            {section.subsections && (
                                                                <div>
                                                                    <h4 className="font-semibold text-gray-900 mb-3 text-base">
                                                                        {lang === "en" ? "Key Features" : "الميزات الرئيسية"}
                                                                    </h4>
                                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                                        {section.subsections.map((sub, subIndex) => {
                                                                            const SubIcon = sub.icon;
                                                                            return (
                                                                                <div 
                                                                                    key={subIndex} 
                                                                                    className="p-3 bg-gray-50 rounded-lg border border-gray-200"
                                                                                >
                                                                                    <div className="flex items-center gap-2 mb-1">
                                                                                        {SubIcon && (
                                                                                            <SubIcon sx={{ fontSize: '16px', color: '#6B7280' }} />
                                                                                        )}
                                                                                        <div className="font-medium text-gray-900 text-sm">{sub.title}</div>
                                                                                    </div>
                                                                                    <div className="text-sm text-gray-600 mt-1">{sub.description}</div>
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {/* Actions Table */}
                                                            {section.actions && (
                                                                <div>
                                                                    <h4 className="font-semibold text-gray-900 mb-3 text-base">
                                                                        {lang === "en" ? "Available Actions" : "الإجراءات المتاحة"}
                                                                    </h4>
                                                                    <div className="overflow-hidden rounded-lg border border-gray-200">
                                                                        <div className="overflow-x-auto">
                                                                            <table className="min-w-full divide-y divide-gray-200">
                                                                                <thead className="bg-gray-50">
                                                                                    <tr>
                                                                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-16 text-center">
                                                                                            {currentDoc.tableHeaders.icon}
                                                                                        </th>
                                                                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                                                                            {currentDoc.tableHeaders.action}
                                                                                        </th>
                                                                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                                                                            {currentDoc.tableHeaders.description}
                                                                                        </th>
                                                                                    </tr>
                                                                                </thead>
                                                                                <tbody className="bg-white divide-y divide-gray-200">
                                                                                    {section.actions.map((action, actionIndex) => (
                                                                                        <tr 
                                                                                            key={actionIndex} 
                                                                                            className="hover:bg-gray-50 transition-colors"
                                                                                        >
                                                                                            <td className="whitespace-nowrap py-4 px-4 text-center">
                                                                                                <div className={`inline-flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br ${colors.iconGradient}`}>
                                                                                                    <span style={{ color: 'white' }}>
                                                                                                        {action.icon}
                                                                                                    </span>
                                                                                                </div>
                                                                                            </td>
                                                                                            <td className="whitespace-nowrap py-4 px-4">
                                                                                                <span className="text-sm font-medium text-gray-900">{action.title}</span>
                                                                                            </td>
                                                                                            <td className="py-4 px-4">
                                                                                                <p className="text-sm text-gray-700 leading-relaxed">{action.description}</p>
                                                                                            </td>
                                                                                        </tr>
                                                                                    ))}
                                                                                </tbody>
                                                                            </table>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Empty State */}
                                {filteredSections.length === 0 && (
                                    <div className="text-center py-16">
                                        <SearchIcon sx={{ fontSize: '64px', color: '#D1D5DB', margin: '0 auto 16px' }} />
                                        <h3 className="text-xl font-bold text-gray-900 mb-2">No results found</h3>
                                        <p className="text-gray-600">Try adjusting your search query</p>
                                    </div>
                                )}
                            </div>
                        </main>
                </div>
            </Dialog>
        </>
    );
}
